import React, { useState } from 'react';
import { ExternalLink, Play, Youtube, Instagram, Twitter, Twitch, Link as LinkIcon, User } from 'lucide-react';

/*
 * Shared presentational pieces. These exist because the same three things were
 * being rebuilt inline with slightly different class strings each time, which
 * is how the public creator cards ended up never showing social links while
 * the admin list did.
 */

/** The YouTube video id out of any of the URL shapes people paste. */
export function youtubeId(url) {
  if (!url) return null;
  const m = String(url).match(
    /(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|shorts\/|watch\?v=|watch\?.+&v=))([A-Za-z0-9_-]{6,})/
  );
  return m ? m[1] : null;
}

/*
 * Video thumbnail.
 *
 * Nothing is stored for this and nothing needs to be: YouTube serves a
 * thumbnail at a fixed address derived from the video id, so every video in
 * the database can have one with no API call and no quota. hqdefault exists
 * for every public video, where maxresdefault does not, so it is the safe
 * choice; a video that has been deleted or made private falls back to the
 * placeholder rather than showing a broken image.
 */
export function VideoThumb({ url, title, className = '', size = 'md' }) {
  const [failed, setFailed] = useState(false);
  const id = youtubeId(url);
  const dims = { sm: 'w-28 h-16', md: 'w-40 h-[90px]', lg: 'w-full aspect-video' }[size] || '';

  if (!id || failed) {
    return (
      <div
        className={`${dims} ${className} shrink-0 rounded-md bg-slate-100 flex items-center justify-center text-slate-400`}
        aria-hidden="true"
      >
        <Play className="w-5 h-5" />
      </div>
    );
  }

  return (
    <div className={`${dims} ${className} shrink-0 relative rounded-md overflow-hidden bg-slate-100 group`}>
      <img
        src={`https://i.ytimg.com/vi/${id}/hqdefault.jpg`}
        alt=""
        loading="lazy"
        onError={() => setFailed(true)}
        // hqdefault is 4:3 with black bars top and bottom; scaling up and
        // cropping gives the 16:9 frame the video actually has.
        className="absolute inset-0 w-full h-full object-cover scale-[1.35]"
      />
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
        <Play className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow" />
      </div>
      {title && <span className="sr-only">{title}</span>}
    </div>
  );
}

const PLATFORM_ICONS = {
  youtube: Youtube,
  instagram: Instagram,
  twitter: Twitter,
  x: Twitter,
  twitch: Twitch,
};

const PLATFORM_STYLES = {
  youtube: 'hover:bg-red-50 hover:text-red-700 hover:border-red-200',
  instagram: 'hover:bg-pink-50 hover:text-pink-700 hover:border-pink-200',
  twitter: 'hover:bg-sky-50 hover:text-sky-700 hover:border-sky-200',
  x: 'hover:bg-slate-100 hover:text-slate-900 hover:border-slate-300',
  twitch: 'hover:bg-purple-50 hover:text-purple-700 hover:border-purple-200',
};

/*
 * Social links as chips.
 *
 * The public creator cards never rendered these at all, which is most of why
 * they looked empty: 135 links existed in the database and none of them were
 * on the card people actually look at.
 */
export function SocialLinks({ links, channel, size = 'sm', className = '' }) {
  const all = [];
  const seen = new Set();

  const push = (platform, url) => {
    if (!url) return;
    const key = String(url).toLowerCase().replace(/\/+$/, '');
    if (seen.has(key)) return;
    seen.add(key);
    all.push({ platform: platform || 'Link', url });
  };

  // A channel URL is a link too, and was previously printed as bare text.
  if (channel) push(/youtube/i.test(channel) ? 'YouTube' : null, channel);
  (links || []).forEach((l) => push(l.platform, l.url));

  if (all.length === 0) return null;

  const pad = size === 'sm' ? 'px-2 py-0.5 text-xs' : 'px-3 py-1 text-sm';
  const icon = size === 'sm' ? 'w-3 h-3' : 'w-3.5 h-3.5';

  return (
    <div className={`flex flex-wrap items-center gap-1.5 ${className}`}>
      {all.map((l, i) => {
        const key = String(l.platform || '').toLowerCase();
        const Icon = PLATFORM_ICONS[key] || LinkIcon;
        const style = PLATFORM_STYLES[key] || 'hover:bg-slate-100 hover:text-slate-900 hover:border-slate-300';
        return (
          <a
            key={i}
            href={l.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            title={l.url}
            className={`inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white text-slate-600 transition-colors ${pad} ${style}`}
          >
            <Icon className={icon} />
            {l.platform}
          </a>
        );
      })}
    </div>
  );
}

/*
 * Creator avatar.
 *
 * 86% of creators have a generated avatar rather than a photo, because most
 * are linked by Instagram and Instagram no longer gives photos out. Since that
 * is the common case rather than the exception, it should look deliberate
 * instead of looking like an image that failed to load: initials on a tint
 * derived from the name, so it is stable per person and reads as a design
 * choice.
 */
const TINTS = [
  'bg-rose-100 text-rose-700',
  'bg-orange-100 text-orange-700',
  'bg-amber-100 text-amber-700',
  'bg-emerald-100 text-emerald-700',
  'bg-teal-100 text-teal-700',
  'bg-sky-100 text-sky-700',
  'bg-indigo-100 text-indigo-700',
  'bg-violet-100 text-violet-700',
];

const initialsOf = (name) =>
  String(name || '?')
    .replace(/[^\p{L}\p{N} ]/gu, '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase() || '?';

const tintOf = (name) => {
  let h = 0;
  for (const ch of String(name || '')) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return TINTS[h % TINTS.length];
};

const isGenerated = (src) => !src || /dicebear|ui-avatars|gravatar/i.test(src);

export function Avatar({ src, name, size = 48, onClick, className = '' }) {
  const [failed, setFailed] = useState(false);
  const px = { width: size, height: size };
  const shared = `rounded-full object-cover shrink-0 ${onClick ? 'cursor-pointer' : ''} ${className}`;

  if (isGenerated(src) || failed) {
    return (
      <div
        style={px}
        onClick={onClick}
        title={name}
        className={`${shared} ${tintOf(name)} flex items-center justify-center font-semibold select-none`}
      >
        {size >= 32 ? (
          <span style={{ fontSize: Math.round(size * 0.36) }}>{initialsOf(name)}</span>
        ) : (
          <User className="w-4 h-4" />
        )}
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={name}
      style={px}
      onClick={onClick}
      onError={() => setFailed(true)}
      loading="lazy"
      className={`${shared} ring-1 ring-black/5`}
    />
  );
}

/** A plain external link that stops the click reaching a clickable card. */
export function OutLink({ href, children, className = '' }) {
  if (!href) return null;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      className={`inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 hover:underline ${className}`}
    >
      <ExternalLink className="w-3.5 h-3.5" />
      {children}
    </a>
  );
}
